/** D03 报关清关 Mock 数据 */

import dayjs from 'dayjs';
import type {
  CustomsDeclaration,
  DestinationClearance,
  ClearanceStep,
  ClearanceDocument,
  InspectionRecord,
  InspectionStatus,
  PhytoStatus,
  DeclarationSubmitStatus,
  ReleaseStatus,
  ClearanceStepStatus,
  DocumentStatus,
} from '../types';

// ===== 辅助工具 =====

const today = dayjs();

const categories: string[] = ['大蒜', '生姜', '苹果', '梨', '胡萝卜', '洋葱', '土豆', '西兰花'];
const ports: string[] = ['上海港', '青岛港', '宁波港', '深圳港', '广州港'];
const agencies: string[] = ['中国检验认证集团', 'SGS通标', '中检山东公司', '华测检测', 'TÜV莱茵'];

const inspectionStatuses: InspectionStatus[] = ['PASSED', 'PASSED', 'PASSED', 'PENDING', 'FAILED'];
const phytoStatuses: PhytoStatus[] = ['PASSED', 'PASSED', 'PASSED', 'PENDING', 'FAILED'];
const declarationSubmitStatuses: DeclarationSubmitStatus[] = ['SUBMITTED', 'APPROVED', 'UNDER_REVIEW', 'REJECTED'];
const releaseStatuses: ReleaseStatus[] = ['RELEASED', 'RELEASED', 'PENDING_RELEASE', 'PENDING_RELEASE', 'RELEASED', 'HELD'];

// ===== 12 条出口报关数据 =====

export const mockCustomsDeclarations: CustomsDeclaration[] = Array.from({ length: 12 }, (_, i) => {
  // 前3条没有报关单号 → 所有状态都是初始态
  const hasDeclaration = i >= 3;
  const inspectionStatus: InspectionStatus = hasDeclaration ? inspectionStatuses[i % inspectionStatuses.length] : 'PENDING';
  const phytoStatus: PhytoStatus = hasDeclaration ? phytoStatuses[(i + 1) % phytoStatuses.length] : 'PENDING';
  const customsDeclarationStatus: DeclarationSubmitStatus = hasDeclaration ? declarationSubmitStatuses[i % declarationSubmitStatuses.length] : 'NOT_SUBMITTED';
  const releaseStatus: ReleaseStatus = hasDeclaration ? releaseStatuses[i % releaseStatuses.length] : 'PENDING_RELEASE';

  return {
    id: `DEC${String(i + 1).padStart(3, '0')}`,
    orderNo: `BP-2025-${String(i + 1001).slice(1)}`,
    containerNo: `BLU-C-${2601 + i}`,
    category: categories[i % categories.length],
    // 前3条没有报关单号（模拟待报关状态）
    declarationNo: hasDeclaration ? `CD-2025-${String(i + 1).padStart(4, '0')}` : '',
    inspectionStatus,
    phytoStatus,
    phytoDate: phytoStatus === 'PASSED'
      ? today.subtract(i + 2, 'day').format('YYYY-MM-DD')
      : '',
    customsDeclarationStatus,
    releaseStatus,
    destinationClearanceId: `DCL${String(i + 1).padStart(3, '0')}`,
  };
});

// ===== 底部面板：6 步清关 Timeline 数据 =====

const clearStepsTemplates: Array<{ name: string; offsetDays: number }> = [
  { name: '到港', offsetDays: -7 },
  { name: 'MOCCAE 申报', offsetDays: -5 },
  { name: 'Municipality 申报', offsetDays: -3 },
  { name: '关税缴纳', offsetDays: -1 },
  { name: '海关清关', offsetDays: 1 },
  { name: '提货/配送', offsetDays: 3 },
];

function generateSteps(declarationIndex: number): ClearanceStep[] {
  // 根据 declaration 索引决定进度 —— 前 4 条偏快，中 4 条正常，后 4 条偏慢
  const progressCutoff: number = declarationIndex < 4 ? 5 : declarationIndex < 8 ? 3 : 2;

  return clearStepsTemplates.map((tmpl, stepIdx) => {
    let status: ClearanceStepStatus;
    if (stepIdx < progressCutoff) {
      status = 'DONE';
    } else if (stepIdx === progressCutoff) {
      status = 'CURRENT';
    } else {
      status = 'PENDING';
    }

    const timestamp: string = status === 'PENDING'
      ? '--'
      : today.subtract(Math.abs(tmpl.offsetDays), 'day').format('YYYY-MM-DD HH:mm');

    return {
      id: `STEP${declarationIndex + 1}_${stepIdx + 1}`,
      name: tmpl.name,
      description: status === 'DONE' ? `${tmpl.name}已完成` : status === 'CURRENT' ? '进行中...' : '待处理',
      status,
      timestamp,
    };
  });
}

// ===== 目的港清关数据（与报关记录一一对应） =====

export const mockDestinationClearances: DestinationClearance[] = Array.from({ length: 12 }, (_, i) => ({
  id: `DCL${String(i + 1).padStart(3, '0')}`,
  declarationId: `DEC${String(i + 1).padStart(3, '0')}`,
  orderNo: `BP-2025-${String(i + 1001).slice(1)}`,
  containerNo: `BLU-C-${2601 + i}`,
  destinationPort: '迪拜港',
  eta: today.subtract(8 - i, 'day').format('YYYY-MM-DDTHH:mm:ss[Z]'),
  steps: generateSteps(i),
  alertInfo: '迪拜进口实施 MOCCAE（气候变化与环境部）+ Municipality（市政局）双机构检验，两机构检验均通过后方可清关提货。',
}));

// ===== 6 项报关资料（CI/PL/PHYTO/BL/CO/GACC）=====

const docTemplates: Array<{ docType: string; docName: string }> = [
  { docType: 'CI', docName: 'CI 商业发票' },
  { docType: 'PL', docName: 'PL 装箱单' },
  { docType: 'PHYTO', docName: 'Phyto 植检证' },
  { docType: 'BL', docName: 'B/L 提单' },
  { docType: 'CO', docName: 'CO 原产地证' },
  { docType: 'GACC', docName: 'GACC 报关单' },
];

/** 按 declarationId 返回其对应的 6 项资料 */
export function getMockClearanceDocuments(declarationId: string): ClearanceDocument[] {
  const declIndex: number = parseInt(declarationId.replace('DEC', ''), 10) || 1;
  const docStatuses: DocumentStatus[][] = [
    ['READY', 'READY', 'PENDING', 'READY', 'READY', 'PENDING'],   // DEC001 — 缺 Phyto + GACC
    ['READY', 'READY', 'READY', 'READY', 'READY', 'READY'],        // DEC002 — 全部齐备
    ['READY', 'PENDING', 'PENDING', 'READY', 'READY', 'MISSING'],  // DEC003 — 缺 PL + Phyto + GACC
    ['READY', 'READY', 'PENDING', 'READY', 'PENDING', 'PENDING'],  // DEC004
    ['READY', 'READY', 'READY', 'READY', 'READY', 'PENDING'],      // DEC005
    ['READY', 'READY', 'PENDING', 'READY', 'READY', 'READY'],      // DEC006
    ['READY', 'PENDING', 'READY', 'READY', 'READY', 'PENDING'],    // DEC007
    ['READY', 'READY', 'READY', 'READY', 'READY', 'MISSING'],      // DEC008
    ['READY', 'READY', 'READY', 'PENDING', 'READY', 'PENDING'],    // DEC009
    ['PENDING', 'READY', 'READY', 'READY', 'PENDING', 'PENDING'], // DEC010
    ['READY', 'READY', 'READY', 'READY', 'READY', 'READY'],        // DEC011
    ['READY', 'READY', 'READY', 'READY', 'READY', 'PENDING'],      // DEC012
  ];

  const statuses: DocumentStatus[] = docStatuses[(declIndex - 1) % docStatuses.length];

  return docTemplates.map((tmpl, idx) => {
    const status: DocumentStatus = statuses[idx];
    return {
      id: `DOC${declarationId.replace('DEC', '')}_${idx + 1}`,
      declarationId,
      docType: tmpl.docType,
      docName: tmpl.docName,
      status,
      uploadDate: status === 'READY'
        ? today.subtract(idx + 1, 'day').format('YYYY-MM-DD')
        : '',
    };
  });
}

/** 默认 12 份资料的注册表（导出共用） */
export const mockClearanceDocuments: ClearanceDocument[] = Array.from({ length: 12 }, (_, i) =>
  getMockClearanceDocuments(`DEC${String(i + 1).padStart(3, '0')}`)
).flat();

// ===== 15 条商检/植检记录 =====

export const mockInspectionRecords: InspectionRecord[] = Array.from({ length: 15 }, (_, i) => {
  const isPhyto: boolean = i >= 7;
  const status: InspectionStatus = inspectionStatuses[i % inspectionStatuses.length];
  return {
    id: `INSP${String(i + 1).padStart(3, '0')}`,
    orderNo: `BP-2025-${String(i + 2001).slice(1)}`,
    containerNo: `BLU-C-${2701 + i}`,
    category: categories[i % categories.length],
    inspectionType: isPhyto ? '植检' : '商检',
    status,
    inspectionDate: status === 'PASSED'
      ? today.subtract(i + 1, 'day').format('YYYY-MM-DD')
      : today.add(i + 1, 'day').format('YYYY-MM-DD'),
    certificateNo: status === 'PASSED'
      ? `CERT-2025-${String(i + 101).padStart(3, '0')}`
      : '',
    result: status === 'PASSED' ? '合格' : status === 'FAILED' ? '不合格' : '待检验',
    agency: agencies[i % agencies.length],
  };
});
